import i18n from "features/shared/i18n";
import { PlanType, EntitlementDetail, EntitlementSourceType } from "features/subscription";
import { getPlanColor } from "./subscriptionTabUtils";

function getPlanName(plan: PlanType): string {
  switch (plan) {
    case PlanType.STANDARD:
      return i18n.t("MSG_PLAN_STANDARD_PARENT");
    case PlanType.PREMIUM:
      return i18n.t("MSG_PLAN_PREMIUM_PARENT");
    case PlanType.BUILDER:
      return i18n.t("MSG_PLAN_BUILDER_PARENT");
    case PlanType.EDUCATION:
      return i18n.t("MSG_PLAN_EDUCATION_PARENT");
    default:
      return i18n.t("MSG_PLAN_FREE_PARENT");
  }
}

function getSourceLabel(entitlement: EntitlementDetail | null): string {
  if (!entitlement) return "-";
  switch (entitlement.payment_provider) {
    case "stripe":
      return "Web";
    case "app_store":
      return "App Store";
    case "play_store":
      return "Google Play";
    default:
      return "-";
  }
}

interface PlanInfoSectionProps {
  plan: PlanType;
  entitlement: EntitlementDetail | null;
  isPaid: boolean;
  isEducation: boolean;
  isNativeApp: boolean;
  onManageSubscription: () => void;
  onRestorePurchases: () => void;
}

export function PlanInfoSection({
  plan,
  entitlement,
  isPaid,
  isEducation,
  onManageSubscription,
  onRestorePurchases,
}: PlanInfoSectionProps) {
  const isManual = entitlement?.source_type === EntitlementSourceType.MANUAL;
  const isFree = !isPaid && !isEducation;

  return (
    <div className="bg-white rounded-[16px] p-6">
      <div className="flex-row-el items-center gap-2 text-[27px] text-textcolor-black mb-5 pb-3 border-b border-outline">
        <span className="material-symbols-outlined !text-[45px] text-red-100">credit_card</span>
        {i18n.t("MSG_PARENT_PAGE_PLAN_INFO")}
      </div>

      <div className="flex-row-el items-center py-3 border-b border-outline">
        <div className="w-[200px] text-[24px] text-textcolor-black">
          {i18n.t("MSG_PARENT_PAGE_CURRENT_PLAN")}
        </div>
        <div className="flex-1 flex-row-el flex-wrap items-center justify-between gap-2">
          <span
            className="inline-flex items-center gap-1 py-1 px-3 rounded-full text-[21px] text-white"
            style={{ backgroundColor: getPlanColor(plan) }}
          >
            <span className="material-symbols-outlined text-[24px]">workspace_premium</span>
            {getPlanName(plan)}
          </span>

          {isFree && (
            <button
              className="flex-row-el items-center gap-1 py-2 px-4 bg-gray2-100 text-white rounded-[8px] text-[21px] border-none cursor-pointer hover:opacity-90 transition-all"
              onClick={onRestorePurchases}
            >
              <span className="material-symbols-outlined text-[24px]">restore</span>
              {i18n.t("MSG_SUBSCRIPTION_RESTORE_BTN")}
            </button>
          )}
        </div>
      </div>

      {isPaid && entitlement && (
        <div className="flex-row-el items-center py-3 border-b border-outline">
          <div className="w-[200px] text-[24px] text-textcolor-black">
            {i18n.t("MSG_PARENT_PAGE_SUBSCRIPTION_SOURCE")}
          </div>
          <div className="flex-1 text-[24px] text-textcolor-black">
            {getSourceLabel(entitlement)}
          </div>
        </div>
      )}

      {isPaid && entitlement && !isManual && (
        <div className="flex-row-el items-center py-3">
          <div className="w-[200px] text-[24px] text-textcolor-black">
            {i18n.t("MSG_PARENT_PAGE_BILLING_PERIOD")}
          </div>
          <div className="flex-1 flex-row-el flex-wrap items-center justify-between gap-2">
            <span className="text-[24px] text-textcolor-black">
              {entitlement.billing_period === "monthly"
                ? i18n.t("MSG_SUBSCRIPTION_MONTHLY_LABEL")
                : i18n.t("MSG_SUBSCRIPTION_ANNUAL_LABEL")}
            </span>
            <button
              className="flex-row-el items-center gap-1 py-2 px-4 bg-blue-100 text-white rounded-[8px] text-[21px] border-none cursor-pointer hover:opacity-90 transition-all"
              onClick={onManageSubscription}
            >
              <span className="material-symbols-outlined text-[24px]">settings</span>
              {i18n.t("MSG_SUBSCRIPTION_MANAGE")}
            </button>
          </div>
        </div>
      )}

      {isEducation && (
        <div className="py-3 text-[21px] text-gray2-100">
          {i18n.t("MSG_PARENT_PAGE_EDUCATION_DESCRIPTION")}
        </div>
      )}
    </div>
  );
}
